import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { 
  Calculator, 
  LogOut,
  ArrowLeft,
  Loader2,
  Save,
  Plus,
  Trash2,
  UserCheck,
  Percent,
  Euro,
  TrendingUp
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-PT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const AdminPermFeeTiers = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tiers, setTiers] = useState([]);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  
  const [formData, setFormData] = useState({
    min_salary: 0,
    max_salary: "",
    fee_percentage: 15,
    description: "",
    order: 0,
    active: true
  });

  useEffect(() => {
    loadTiers();
  }, []);

  const loadTiers = async () => {
    try {
      const response = await axios.get(`${API}/perm-fee-tiers`, { headers: getAuthHeaders() });
      setTiers(response.data);
    } catch (error) {
      console.error("Error loading tiers:", error);
      toast.error("Erro ao carregar escalões");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/");
    toast.success("Sessão terminada");
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        min_salary: parseFloat(formData.min_salary) || 0,
        max_salary: formData.max_salary ? parseFloat(formData.max_salary) : null,
        fee_percentage: parseFloat(formData.fee_percentage) || 15,
        description: formData.description || "",
        order: parseInt(formData.order) || tiers.length + 1,
        active: formData.active
      };

      if (editingTier) {
        await axios.put(`${API}/perm-fee-tiers/${editingTier.id}`, payload, { headers: getAuthHeaders() });
        toast.success("Escalão atualizado!");
      } else {
        await axios.post(`${API}/perm-fee-tiers`, payload, { headers: getAuthHeaders() });
        toast.success("Escalão criado!");
      }
      
      setShowAddDialog(false);
      setEditingTier(null);
      resetForm();
      loadTiers();
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao guardar";
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tierId) => {
    if (!confirm("Tem a certeza que quer eliminar este escalão?")) return;
    
    try {
      await axios.delete(`${API}/perm-fee-tiers/${tierId}`, { headers: getAuthHeaders() });
      toast.success("Escalão eliminado!");
      loadTiers();
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao eliminar";
      toast.error(message);
    }
  };

  const handleEdit = (tier) => {
    setEditingTier(tier);
    setFormData({
      min_salary: tier.min_salary || 0,
      max_salary: tier.max_salary || "",
      fee_percentage: tier.fee_percentage || 15,
      description: tier.description || "",
      order: tier.order || 0,
      active: tier.active !== false
    });
    setShowAddDialog(true);
  };

  const resetForm = () => {
    setFormData({
      min_salary: 0,
      max_salary: "",
      fee_percentage: 15,
      description: "",
      order: tiers.length + 1,
      active: true
    });
  };

  const handleSeedDefaults = async () => {
    try {
      const response = await axios.post(`${API}/perm-fee-tiers/seed-defaults`, {}, { headers: getAuthHeaders() });
      toast.success(response.data.message);
      loadTiers();
    } catch (error) {
      const message = error.response?.data?.detail || "Erro ao criar escalões";
      toast.error(message);
    }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="admin-perm-fee-tiers">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-slate-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-brand-600 flex items-center justify-center">
              <Calculator className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="font-manrope font-bold text-lg text-foreground">Evolutt</span>
              <p className="text-xs text-muted-foreground">Escalões Fee Perm</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">{user?.name}</span>
            </span>
            <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-btn">
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Back Button & Title */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
            </Link>
            <div>
              <h1 className="font-manrope font-extrabold text-2xl text-foreground flex items-center gap-2">
                <UserCheck className="w-6 h-6 text-blue-500" />
                Escalões Fee Perm
              </h1>
              <p className="text-muted-foreground">Tabela de comissões por escalão salarial</p>
            </div>
          </div>
          
          <Button
            onClick={() => {
              setEditingTier(null);
              resetForm();
              setShowAddDialog(true);
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo Escalão
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Info Box — texto contraditório removido. A lógica REAL é: quanto maior a
                remuneração anual, MAIOR a % de fee (escalões crescentes definidos abaixo). */}

            {/* Tiers Table */}
            {tiers.length === 0 ? (
              <Card className="bg-white border border-border/60 shadow-sm">
                <CardContent className="p-8 text-center">
                  <TrendingUp className="w-12 h-12 mx-auto text-slate-300 mb-4" />
                  <p className="text-muted-foreground mb-4">Nenhum escalão configurado.</p>
                  <Button onClick={handleSeedDefaults} variant="outline">
                    Carregar Escalões Padrão
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-white border border-border/60 shadow-sm overflow-hidden">
                <CardHeader className="bg-slate-50 border-b">
                  <CardTitle className="text-base flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-blue-500" />
                    Tabela de Escalões ({tiers.length})
                  </CardTitle>
                </CardHeader>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-100">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">#</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Salário Mínimo</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Salário Máximo</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Fee %</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase">Descrição</th>
                        <th className="px-4 py-3 text-center text-xs font-semibold text-slate-600 uppercase">Ações</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {tiers.sort((a, b) => a.order - b.order).map((tier, index) => (
                        <tr key={tier.id} className="hover:bg-slate-50 transition-colors">
                          <td className="px-4 py-3 text-sm font-mono text-slate-500">{tier.order || index + 1}</td>
                          <td className="px-4 py-3 text-sm font-mono">{formatCurrency(tier.min_salary || 0)}</td>
                          <td className="px-4 py-3 text-sm font-mono">
                            {tier.max_salary ? formatCurrency(tier.max_salary) : <span className="text-brand-600 font-semibold">Sem limite</span>}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-bold bg-blue-100 text-blue-800">
                              {tier.fee_percentage}%
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">{tier.description || "-"}</td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEdit(tier)}
                                className="text-slate-600 hover:text-blue-600"
                              >
                                Editar
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(tier.id)}
                                className="text-slate-400 hover:text-red-600"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}

            {/* Example Calculation */}
            {tiers.length > 0 && (
              <Card className="bg-gradient-to-br from-brand-50 to-brand-50 border-brand-200">
                <CardHeader>
                  <CardTitle className="text-base text-brand-800 flex items-center gap-2">
                    <Euro className="w-5 h-5" />
                    Exemplo de Cálculo
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[30000, 50000, 100000].map(salary => {
                      const tier = tiers.find(t => 
                        salary >= (t.min_salary || 0) && 
                        (t.max_salary === null || salary < t.max_salary)
                      );
                      const fee = tier ? salary * tier.fee_percentage / 100 : salary * 0.15;
                      const pct = tier ? tier.fee_percentage : 15;
                      
                      return (
                        <div key={salary} className="bg-white/50 rounded-lg p-4 text-center">
                          <div className="text-sm text-brand-600 mb-1">Salário Anual</div>
                          <div className="text-lg font-bold text-slate-800">{formatCurrency(salary)}</div>
                          <div className="text-xs text-slate-500 mt-2">Fee {pct}%</div>
                          <div className="text-lg font-bold text-brand-700">{formatCurrency(fee)}</div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </main>

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-manrope font-bold text-xl flex items-center gap-2">
              <Percent className="w-5 h-5 text-blue-500" />
              {editingTier ? "Editar Escalão" : "Novo Escalão"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Salário Mínimo (€)</Label>
                <div className="relative">
                  <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="number"
                    min="0"
                    step="1000"
                    placeholder="0"
                    className="pl-9 font-mono"
                    value={formData.min_salary}
                    onChange={(e) => setFormData({ ...formData, min_salary: e.target.value })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Salário Máximo (€)</Label>
                <div className="relative">
                  <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="number"
                    min="0"
                    step="1000"
                    placeholder="Sem limite"
                    className="pl-9 font-mono"
                    value={formData.max_salary}
                    onChange={(e) => setFormData({ ...formData, max_salary: e.target.value })}
                  />
                </div>
                <p className="text-xs text-muted-foreground">Deixe vazio para sem limite</p>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Percentagem de Fee</Label>
              <div className="relative max-w-[150px]">
                <Input
                  type="number"
                  min="0"
                  max="100"
                  step="0.5"
                  placeholder="15"
                  className="pr-8 font-mono text-lg font-bold"
                  value={formData.fee_percentage}
                  onChange={(e) => setFormData({ ...formData, fee_percentage: e.target.value })}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground font-bold">%</span>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Descrição (opcional)</Label>
              <Input
                type="text"
                placeholder="Ex: Até 25k€"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Ordem</Label>
              <Input
                type="number"
                min="1"
                placeholder="1"
                className="max-w-[100px] font-mono"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: e.target.value })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancelar
            </Button>
            <Button 
              onClick={handleSave}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              {editingTier ? "Guardar" : "Criar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPermFeeTiers;
